import React, { useState } from "react";
import DiscountForm from "./discount-form/DiscountForm";
import VariantSelector from "./VariantSelector";
import { getLocalizedProductName } from "../../../../../utils/multilingualUtils";
import { useLanguage } from "../../../../../context/LanguageContext";
import { useAdminProductsTranslation } from "../../translations";

const DiscountModal = ({
    selectedProduct,
    selectedCount,
    discountData,
    setDiscountData,
    onSubmit,
    onClose,
    calculateDiscountedPrice
}) => {
    const { language } = useLanguage();
    const { t } = useAdminProductsTranslation(language);

    const [discountTarget, setDiscountTarget] = useState("product"); // "product" or "variant"
    const [selectedVariants, setSelectedVariants] = useState([]);

    const isBulkDiscount = selectedProduct === "bulk";
    const hasVariants = selectedProduct?.variants && selectedProduct.variants.length > 0;

    const getTitle = () => {
        if (isBulkDiscount) {
            return t('addDiscountToProducts', { count: selectedCount });
        }

        if (discountTarget === "variant" && selectedVariants.length > 0) {
            return t('addDiscountToVariants', {
                count: selectedVariants.length,
                productName: getLocalizedProductName(selectedProduct, 'en')
            });
        }

        return `${t('addDiscountTo')} "${getLocalizedProductName(selectedProduct, 'en')}"`;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Pass additional data about target and variants to the parent
        const submitData = {
            ...discountData,
            target: discountTarget,
            selectedVariants: discountTarget === "variant" ? selectedVariants : []
        };

        onSubmit(e, submitData);
    };

    return (
        <div className="modal-overlay">
            <div className="discount-modal">
                <div className="modal-header">
                    <h2>{getTitle()}</h2>
                    <button className="modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="modal-content">
                    {/* Target Selection - only show for single products with variants */}
                    {!isBulkDiscount && hasVariants && (
                        <div className="discount-target-section">
                            <h3>Apply Discount To:</h3>
                            <div className="target-options">
                                <label className="target-option">
                                    <input
                                        type="radio"
                                        value="product"
                                        checked={discountTarget === "product"}
                                        onChange={(e) => {
                                            setDiscountTarget(e.target.value);
                                            setSelectedVariants([]);
                                        }}
                                    />
                                    <span>Entire Product</span>
                                    <small>Apply discount to all variants</small>
                                </label>

                                <label className="target-option">
                                    <input
                                        type="radio"
                                        value="variant"
                                        checked={discountTarget === "variant"}
                                        onChange={(e) => setDiscountTarget(e.target.value)}
                                    />
                                    <span>Specific Variants</span>
                                    <small>Apply discount to selected variants only</small>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Variant Selection */}
                    {discountTarget === "variant" && hasVariants && (
                        <VariantSelector
                            variants={selectedProduct.variants}
                            options={selectedProduct.options}
                            selectedVariants={selectedVariants}
                            onVariantSelect={setSelectedVariants}
                        />
                    )}

                    <DiscountForm
                        discountData={discountData}
                        setDiscountData={setDiscountData}
                        onSubmit={handleSubmit}
                        onCancel={onClose}
                        selectedProduct={selectedProduct}
                        calculateDiscountedPrice={calculateDiscountedPrice}
                        isBulk={isBulkDiscount}
                        discountTarget={discountTarget}
                        selectedVariants={selectedVariants}
                    />
                </div>
            </div>
        </div>
    );
};

export default DiscountModal;